// Correction lines: one per product, a positive quantity in one of its units; the line's reason adds or removes it.
// The cost comes from the site's stock layers (FIFO), else the supplier; the API works it out (POST /inventory-corrections/costs).
import { type ProductUnit, defaultUnitId, factorOf, unitsOfProduct } from '../../components/purchasing/units';
import { parseNumber, priceText, qtyText } from '../Order/orderLineRules';

export interface CorrectionReason {
  id: string;
  name: string;
  /** 1 adds stock; anything else removes it. */
  impactType: number | null;
}

export interface CorrectionLine {
  /** The product id: the API keeps one line per product. */
  key: string;
  productId: string;
  name: string;
  sku: string;
  group: string;
  units: ProductUnit[];
  productUOMId: string;
  reasonId: string;
  /** As typed. */
  qty: string;
  /** Per unit of the line: the API's cost, or what the user typed where the API gives none. */
  cost: string;
  /** Where the cost comes from; null until the API has answered. */
  costSource: CostSource | null;
  /** Only a 0.00 cost that no stock layer gives is entered by hand. */
  costEditable: boolean;
  /** The costKey the API answered for; another key asks again. */
  costFor: string | null;
}

export type CostSource = 'stock' | 'supplier' | 'none';

export interface LineCost {
  cost: number;
  source: CostSource;
  editable: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

export const signOf = (reason: CorrectionReason | undefined | null): 1 | -1 => (Number(reason?.impactType) === 1 ? 1 : -1);

export const reasonOf = (detail: any): CorrectionReason | null =>
  detail?.reason?.id ? { id: String(detail.reason.id), name: String(detail.reason.reasonName ?? ''), impactType: detail.reason.impactType ?? null } : null;

export const lineOfDetail = (detail: any): CorrectionLine => {
  const units = unitsOfProduct(detail?.product);
  const unitId = String(detail?.productUOMId ?? '');
  return {
    key: String(detail?.productId ?? ''),
    productId: String(detail?.productId ?? ''),
    name: detail?.product?.productName ?? '',
    sku: detail?.product?.sku ?? '',
    group: detail?.product?.productGroup?.groupName ?? '',
    units,
    productUOMId: unitId,
    reasonId: String(detail?.reasonId ?? ''),
    qty: qtyText(Math.abs(Number(detail?.quantity) || 0)),
    cost: priceText(Number(detail?.cost) || 0),
    costSource: null,
    costEditable: false,
    costFor: null,
  };
};

/** A product from `/products/list-for-inventory-correction`, in the supplier's unit else the base unit; its cost comes from the API. */
export const lineOfPick = (product: any, unitId?: string): CorrectionLine => {
  const units = unitsOfProduct(product);
  const supplier = product?.supplierInventory ?? null;
  const supplierUnit = supplier?.productUOMId && units.some((unit) => unit.id === String(supplier.productUOMId)) ? String(supplier.productUOMId) : '';
  const productUOMId = unitId || supplierUnit || defaultUnitId(units);
  return {
    key: String(product?.id ?? ''),
    productId: String(product?.id ?? ''),
    name: product?.productName ?? '',
    sku: product?.sku ?? '',
    group: product?.productGroup?.groupName ?? '',
    units,
    productUOMId,
    reasonId: '',
    qty: '',
    cost: '',
    costSource: null,
    costEditable: false,
    costFor: null,
  };
};

const qtyOf = (line: CorrectionLine): number => {
  const q = parseNumber(line.qty);
  return q !== null && Number.isFinite(q) ? Math.abs(q) : 0;
};

/** What the API is asked for this line at the site; a change of it asks again. */
export const costKey = (line: CorrectionLine, siteId: string): string => [siteId, line.productId, line.productUOMId, qtyOf(line), line.reasonId].join('|');

/** One line of POST /inventory-corrections/costs; empty ids are left out. */
export const costRequest = (line: CorrectionLine) => ({
  productId: line.productId,
  ...(line.productUOMId ? { productUOMId: line.productUOMId } : {}),
  quantity: qtyOf(line),
  ...(line.reasonId ? { reasonId: line.reasonId } : {}),
});

/**
 * The API's answer for `askedFor`: a known cost replaces the line's; a cost to enter keeps what was typed or saved,
 * except one left by an earlier known cost, which starts again at 0.00.
 */
export const withLineCost = (line: CorrectionLine, known: LineCost, askedFor: string): CorrectionLine => {
  if (!known.editable) return { ...line, cost: priceText(known.cost), costSource: known.source, costEditable: false, costFor: askedFor };
  const fromKnown = line.costSource !== null && !line.costEditable;
  const keep = !fromKnown && line.cost.trim() !== '';
  return { ...line, cost: keep ? line.cost : priceText(0), costSource: known.source, costEditable: true, costFor: askedFor };
};

/** Another unit: a typed cost keeps its cost per base unit; a known one waits for the API's answer. */
export const withUnit = (line: CorrectionLine, productUOMId: string): CorrectionLine => {
  if (!line.costEditable) return { ...line, productUOMId, cost: '' };
  const n = parseNumber(line.cost);
  const cost = n !== null && Number.isFinite(n) ? priceText(round2((n / factorOf(line.units, line.productUOMId)) * factorOf(line.units, productUOMId))) : line.cost;
  return { ...line, productUOMId, cost };
};

export interface CorrectionCheck {
  reasonError: string;
  qtyError: string;
  costError: string;
  blocking: boolean;
  /** Signed; null until reason, quantity and cost are all valid. */
  value: number | null;
}

/** `flagged` also reports what is still empty (after a save attempt). */
export const checkLine = (line: CorrectionLine, reasons: Map<string, CorrectionReason>, flagged: boolean): CorrectionCheck => {
  const reason = reasons.get(line.reasonId);
  const reasonError = !line.reasonId ? (flagged ? 'Choose a reason' : '') : '';
  const q = parseNumber(line.qty);
  const qtyError = q === null ? (flagged ? 'Must be over 0' : '') : Number.isNaN(q) ? 'Enter a number' : q <= 0 ? 'Must be over 0' : '';
  const c = parseNumber(line.cost);
  const costError = !line.costEditable ? '' : c === null ? (flagged ? 'Enter a cost' : '') : Number.isNaN(c) ? 'Enter a number' : c < 0 ? "Can't be negative" : '';
  const valid = !!line.reasonId && q !== null && !qtyError && c !== null && !Number.isNaN(c) && !costError;
  return {
    reasonError,
    qtyError,
    costError,
    blocking: !!(reasonError || qtyError || costError),
    value: valid ? round2(signOf(reason) * (q as number) * (c as number)) : null,
  };
};

export const linesPayload = (lines: CorrectionLine[]) =>
  lines.map((line) => ({
    productId: line.productId,
    quantity: Math.abs(parseNumber(line.qty) ?? 0),
    cost: parseNumber(line.cost) ?? 0,
    reasonId: line.reasonId,
    productUOMId: line.productUOMId,
  }));

/** Added, removed or edited lines, by product. */
export const changesOf = (lines: CorrectionLine[], saved: CorrectionLine[]): { kind: 'added' | 'removed' | 'edited'; key: string }[] => {
  const before = new Map(saved.map((line) => [line.key, line]));
  const now = new Set(lines.map((line) => line.key));
  const out: { kind: 'added' | 'removed' | 'edited'; key: string }[] = [];
  lines.forEach((line) => {
    const old = before.get(line.key);
    if (!old) out.push({ kind: 'added', key: line.key });
    // A cost the API works out is not an edit; only a typed one is.
    else if (old.productUOMId !== line.productUOMId || old.reasonId !== line.reasonId || parseNumber(old.qty) !== parseNumber(line.qty) || (line.costEditable && parseNumber(old.cost) !== parseNumber(line.cost)))
      out.push({ kind: 'edited', key: line.key });
  });
  saved.forEach((line) => {
    if (!now.has(line.key)) out.push({ kind: 'removed', key: line.key });
  });
  return out;
};
